import React, { useEffect, useState } from "react";
import { Box, Button, Typography, keyframes } from "@mui/material";
import {
  Check as CheckIcon,
  VolumeUp as ClueIcon,
  North as HigherIcon,
  South as LowerIcon,
  DragHandle as TieIcon,
} from "@mui/icons-material";
import FlippableCard from "../../../games/FlippableCard";
import { HOL, holClueBtnSx, holFadeUp, holPulse } from "../../../UserSide/pages/holTheme";

export const HOL_LIVE_STEPS = ["Hot Seat", "Start Round", "Prediction", "Reveal", "Results"];

export const RESULT_META = {
  correct: { label: "CORRECT", detail: "+1 point", color: HOL.higher },
  tie: { label: "TIE", detail: "+2 points", color: HOL.gold },
  push: { label: "PUSH", detail: "Same value, no point", color: HOL.gold },
  wrong: { label: "MISS", detail: "No point", color: HOL.lower },
};

export const GUESS_META = {
  higher: { label: "HIGHER", color: HOL.higher, Icon: HigherIcon },
  lower: { label: "LOWER", color: HOL.lower, Icon: LowerIcon },
  tie: { label: "TIE", color: HOL.gold, Icon: TieIcon },
};

export const cardLabel = (card) => {
  if (!card) return "card";
  const name = card.displayName || card.name;
  return card.suit ? `${name} of ${card.suit}s` : name;
};

const resultPop = keyframes`
  0% { opacity: 0; transform: scale(0.6); }
  60% { opacity: 1; transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
`;

const liveBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

export const LiveDot = () => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.4,
      borderRadius: "999px",
      bgcolor: "rgba(255,70,70,0.14)",
      border: "1px solid rgba(255,70,70,0.45)",
    }}
  >
    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#ff4646", animation: `${liveBlink} 1.4s ease-in-out infinite` }} />
    <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: "0.72rem", letterSpacing: "0.14em", color: "#ff7a7a" }}>
      LIVE
    </Typography>
  </Box>
);

/** Prediction cycle tracker for the live dashboard. */
export const StageTracker = ({ activeIndex }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 0.75 }, flexWrap: "wrap" }}>
    {HOL_LIVE_STEPS.map((label, i) => {
      const done = i < activeIndex;
      const active = i === activeIndex;
      return (
        <React.Fragment key={label}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.1,
              py: 0.45,
              borderRadius: "999px",
              bgcolor: active ? "rgba(245,215,110,0.16)" : "transparent",
              border: `1px solid ${active ? `${HOL.gold}88` : "transparent"}`,
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: done ? HOL.higher : active ? HOL.gold : "transparent",
                border: done || active ? "none" : "1.5px solid rgba(255,255,255,0.3)",
                color: "#0a0a0a",
              }}
            >
              {done ? <CheckIcon sx={{ fontSize: 12 }} /> : null}
            </Box>
            <Typography
              sx={{
                fontSize: "0.76rem",
                fontWeight: active ? 800 : 600,
                color: active ? "#fff" : done ? HOL.higher : "rgba(255,255,255,0.42)",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>
          {i < HOL_LIVE_STEPS.length - 1 ? (
            <Box sx={{ width: 10, height: 1.5, bgcolor: done ? HOL.higher : "rgba(255,255,255,0.15)" }} />
          ) : null}
        </React.Fragment>
      );
    })}
  </Box>
);

/** Big card that flips face-up whenever `cardKey` changes. */
export const SpotlightCard = ({ card, cardKey, label, accent = HOL.gold, mystery = false, footer = null }) => {
  const [shown, setShown] = useState(false);
  const hasCard = !!card;

  useEffect(() => {
    setShown(false);
    if (!hasCard) return undefined;
    const t = setTimeout(() => setShown(true), 90);
    return () => clearTimeout(t);
  }, [cardKey, hasCard]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 800, fontSize: "0.72rem", letterSpacing: "0.16em", color: accent }}>
        {label}
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: { xs: 118, sm: 150, md: 170 },
          aspectRatio: "5 / 7",
          borderRadius: "14px",
          boxShadow: card ? `0 0 0 2px ${accent}66, 0 22px 44px rgba(0,0,0,0.55)` : "0 22px 44px rgba(0,0,0,0.45)",
          transition: "box-shadow 0.3s",
        }}
      >
        <FlippableCard card={card} revealed={shown && !!card} maxHeight="100%" minHeight={0} />
        {mystery ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              sx={{
                fontFamily: HOL.fontDisplay,
                fontWeight: 900,
                fontSize: { xs: "3rem", md: "4rem" },
                color: "#fff",
                textShadow: "0 4px 24px rgba(0,0,0,0.8)",
                animation: `${holPulse} 2s ease-in-out infinite`,
              }}
            >
              ?
            </Typography>
          </Box>
        ) : null}
      </Box>
      {footer}
    </Box>
  );
};

export const ClueButton = ({ onClick, disabled }) => (
  <Button
    size="small"
    variant="outlined"
    startIcon={<ClueIcon sx={{ fontSize: 14 }} />}
    onClick={onClick}
    disabled={disabled}
    sx={{ ...holClueBtnSx(!disabled), px: 1.25, fontSize: "0.72rem" }}
  >
    Play clue
  </Button>
);

/** Animated result of the round that was just revealed. */
export const ResultBanner = ({ result, roundNumber, animKey }) => {
  const meta = RESULT_META[result];
  if (!meta) return null;
  return (
    <Box
      key={animKey}
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        px: 2,
        py: 1,
        borderRadius: "16px",
        bgcolor: `${meta.color}1c`,
        border: `1px solid ${meta.color}77`,
        animation: `${resultPop} 0.5s cubic-bezier(0.22,1,0.36,1) both`,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.12em", color: HOL.muted }}>
        ROUND {roundNumber}
      </Typography>
      <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 900, fontSize: { xs: "1.3rem", sm: "1.6rem" }, color: meta.color, lineHeight: 1.1 }}>
        {meta.label}
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.7)" }}>{meta.detail}</Typography>
    </Box>
  );
};

/** Whole run at a glance: revealed cards flip in place, with a result dot per round. */
export const DeckStrip = ({ deck, revealedCount, totalCards, rounds = [], runKey }) => (
  <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 0.75, sm: 1 }, flexWrap: "wrap" }}>
    {Array.from({ length: totalCards }).map((_, idx) => {
      const isRevealed = idx < revealedCount;
      const isCurrent = idx === revealedCount - 1 && revealedCount < totalCards;
      const isNext = idx === revealedCount && revealedCount >= 1;
      const round = idx >= 1 ? rounds[idx - 1] : null;
      const dot = round ? RESULT_META[round.result]?.color : null;
      return (
        <Box key={`${runKey}-${idx}`} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: { xs: 40, sm: 50 },
              aspectRatio: "5 / 7",
              borderRadius: "6px",
              outline: isCurrent ? `2px solid ${HOL.gold}` : isNext ? `2px dashed ${HOL.gold}88` : "none",
              outlineOffset: 2,
              opacity: isRevealed || isNext ? 1 : 0.5,
              transition: "opacity 0.3s",
            }}
          >
            <FlippableCard card={isRevealed ? deck[idx] : null} revealed={isRevealed} maxHeight="100%" minHeight={0} durationMs={500} />
          </Box>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot || "rgba(255,255,255,0.12)" }} />
        </Box>
      );
    })}
  </Box>
);

/** The hot seat player's calls this run ("player responses"). */
export const CallLog = ({ rounds = [], pendingGuess = null, pendingRound = null }) => {
  if (!rounds.length && !pendingGuess) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: "center" }}>
      {rounds.map((r) => {
        const guess = GUESS_META[r.user_guess] || GUESS_META.higher;
        const res = RESULT_META[r.result];
        return (
          <Box
            key={r.round_number}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.35,
              borderRadius: "999px",
              fontSize: "0.72rem",
              fontWeight: 700,
              bgcolor: "rgba(255,255,255,0.04)",
              border: `1px solid ${res?.color || HOL.panelBorder}55`,
              animation: `${holFadeUp} 0.35s ease both`,
            }}
          >
            <Box component="span" sx={{ color: HOL.muted }}>R{r.round_number}</Box>
            <guess.Icon sx={{ fontSize: 13, color: guess.color }} />
            <Box component="span" sx={{ color: guess.color }}>{guess.label}</Box>
            <Box component="span" sx={{ color: res?.color }}>· {res?.label}</Box>
          </Box>
        );
      })}
      {pendingGuess ? (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.35,
            borderRadius: "999px",
            fontSize: "0.72rem",
            fontWeight: 800,
            color: GUESS_META[pendingGuess]?.color,
            border: `1px dashed ${GUESS_META[pendingGuess]?.color}`,
            animation: `${holPulse} 1.8s ease-in-out infinite`,
          }}
        >
          R{pendingRound} {GUESS_META[pendingGuess]?.label} · LOCKED
        </Box>
      ) : null}
    </Box>
  );
};
